/**
 * Two-factor enrolment.
 *
 * Two steps on purpose: a secret is issued, then a code proves the
 * authenticator actually works before it is activated. Activating on issue
 * would lock people out the moment a QR failed to scan.
 *
 * The recovery codes are shown exactly once. Only their hashes are stored, so
 * there is no "show them again" — the screen says so plainly rather than
 * letting somebody close the panel and find out later.
 */

import { useState } from "react";
import { ApiError } from "../../lib/api";
import { auth } from "../../lib/auth";

interface Props {
  onDone: () => void;
  onCancel?: () => void;
}

export function MfaEnrolment({ onDone, onCancel }: Props) {
  const [secret, setSecret] = useState<string | null>(null);
  const [uri, setUri] = useState<string>("");
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function begin() {
    setBusy(true);
    setError(null);
    try {
      const result = await auth.enrolMfa();
      setSecret(result.secret);
      setUri(result.provisioning_uri);
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const result = await auth.confirmMfa(code);
      setCodes(result.recovery_codes);
      setNote(result.note);
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  function copyCodes() {
    if (codes) void navigator.clipboard?.writeText(codes.join("\n"));
  }

  return (
    <div className="card mfa">
      <h3 className="section-title">Set up two-factor authentication</h3>
      <p className="section-note">
        Required for approvers — the roles that verify variances, sign stock
        counts and approve Tally batches. A stolen password should not reach
        them.
      </p>

      {error && <div className="banner banner--negative">{error}</div>}

      {!secret && (
        <div className="page__footer">
          <span className="entrytotals__sub">
            You will need an authenticator app — Google Authenticator, Authy,
            or your password manager.
          </span>
          <button className="btn btn--auto" onClick={begin} disabled={busy}>
            {busy ? <span className="spinner" /> : null}
            Begin
          </button>
        </div>
      )}

      {secret && !codes && (
        <>
          <ol className="mfa__steps">
            <li>
              <strong>Add the account to your authenticator.</strong>
              <p className="section-note">
                Scan the link below, or type the key in by hand.
              </p>
              <code className="mfa__secret">{secret}</code>
              <p className="section-note">
                <a href={uri} className="mfa__link">
                  Open in your authenticator app
                </a>
              </p>
            </li>
            <li>
              <strong>Enter the six-digit code it shows.</strong>
              <p className="section-note">
                This proves the authenticator works before it is switched on.
              </p>
              <input
                className="num mono mfa__code"
                inputMode="numeric"
                maxLength={6}
                value={code}
                placeholder="000000"
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
              />
            </li>
          </ol>

          <div className="page__footer">
            {onCancel && (
              <button className="btn btn--ghost btn--auto" onClick={onCancel}>
                Later
              </button>
            )}
            <button
              className="btn btn--auto"
              onClick={confirm}
              disabled={busy || code.length < 6}
            >
              {busy ? <span className="spinner" /> : null}
              Activate
            </button>
          </div>
        </>
      )}

      {codes && (
        <>
          <div className="banner banner--warning">{note}</div>
          <div className="mfa__codes">
            {codes.map((c) => (
              <code key={c}>{c}</code>
            ))}
          </div>
          <div className="page__footer">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={saved}
                onChange={(e) => setSaved(e.target.checked)}
              />
              I have stored these somewhere safe
            </label>
            <button className="btn btn--ghost btn--auto" onClick={copyCodes}>
              Copy
            </button>
            <button
              className="btn btn--auto"
              onClick={onDone}
              disabled={!saved}
              title={saved ? "Finish" : "Confirm you have stored the codes"}
            >
              Done
            </button>
          </div>
        </>
      )}
    </div>
  );
}
